import * as React from "react";
import type { PaletteMode } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme } from "@mui/material/styles";

const STORAGE_KEY = "theme";

export type ColorModeContextValue = {
  mode: PaletteMode;
  toggleColorMode: () => void;
};

const ColorModeContext = React.createContext<ColorModeContextValue>({
  mode: "light",
  toggleColorMode: () => {},
});

function readStoredMode(): PaletteMode | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    // Storage can throw when blocked (e.g. some private-browsing modes).
    return null;
  }
}

// Layout effect in the browser so the saved mode lands before the first paint
// after hydration; plain effect on the server, where layout effects warn.
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

export function ColorModeProvider({ children }: { children: React.ReactNode }) {
  // Always start light so the prerendered HTML and the first client render
  // match; the stored preference is applied right after hydration.
  const [mode, setMode] = React.useState<PaletteMode>("light");

  useIsomorphicLayoutEffect(() => {
    const stored = readStoredMode();
    if (stored) {
      setMode(stored);
    }
  }, []);

  // Keep other open tabs in step when the toggle is used in one of them.
  React.useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        setMode(readStoredMode() ?? "light");
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const toggleColorMode = React.useCallback(() => {
    setMode((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Not persisting is fine; the toggle still works for this visit.
      }
      return next;
    });
  }, []);

  const theme = React.useMemo(() => createTheme({ palette: { mode } }), [mode]);
  const value = React.useMemo(
    () => ({ mode, toggleColorMode }),
    [mode, toggleColorMode]
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export const useColorMode = () => React.useContext(ColorModeContext);
